"use client";

import { Handshake } from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";
import Link from "next/link";
import { MotionIcon } from "@/components/motion/MotionIcon";
import { cn } from "@/lib/utils";

interface NavDonateButtonProps {
  className?: string;
  onClick?: () => void;
  fullWidth?: boolean;
  variant?: "green" | "accent";
  label?: string;
}

export function NavDonateButton({
  className,
  onClick,
  fullWidth = false,
  variant = "green",
  label = "Support Our Mission",
}: NavDonateButtonProps) {
  const reduced = useReducedMotion();

  return (
    <motion.div
      whileHover={reduced ? undefined : { y: -2, scale: 1.03 }}
      whileTap={reduced ? undefined : { scale: 0.97 }}
      transition={{ type: "spring", stiffness: 400, damping: 24 }}
      className={cn("inline-flex", fullWidth && "w-full")}
    >
      <Link
        href="/donate"
        onClick={onClick}
        className={cn(
          "inline-flex items-center justify-center gap-2 rounded-full px-4 py-2.5 text-sm font-semibold text-white transition-shadow",
          "shadow-[0_0_20px_rgba(45,122,74,0.35)] hover:shadow-[0_4px_28px_rgba(45,122,74,0.5)]",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--programs-green)] focus-visible:ring-offset-2",
          variant === "accent"
            ? "bg-gradient-to-r from-[var(--charity-gold)] via-[var(--programs-green)] to-[var(--programs-green-dark)] text-gray-900 hover:from-[var(--charity-gold-dark)] hover:via-[var(--programs-green)] hover:to-[var(--programs-green-dark)] hover:text-white"
            : "bg-gradient-to-r from-[var(--programs-green)] to-[var(--programs-green-dark)]",
          !reduced && "donate-pulse",
          fullWidth && "w-full py-3.5",
          className
        )}
      >
        <MotionIcon icon={Handshake} size={16} hoverScale={1.2} />
        {label === "Support Our Mission" ? (
          <>
            <span className="xl:hidden">Donate Now</span>
            <span className="hidden xl:inline">Support Our Mission</span>
          </>
        ) : (
          label
        )}
      </Link>
    </motion.div>
  );
}
